import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { ZInput } from "@/components/ZInput";
import ZButton from "@/components/ZButton";
import Modal from "@/components/Modal";
import { UserPlus, Copy, AlertTriangle, Trash2, ArrowLeft } from "lucide-react";

export default function AgentsManagePage() {
  const { eventId } = useParams();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showSuccess, setShowSuccess] = useState(null);
  const [showDelete, setShowDelete] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => {
    api.get(`/organizer/events/${eventId}/agents`)
      .then((r) => setAgents(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [eventId]);

  const invite = async () => {
    setErr("");
    if (!form.name.trim() || !form.email.trim()) return setErr("Nom et e-mail obligatoires.");
    setSubmitting(true);
    try {
      const r = await api.post(`/organizer/events/${eventId}/agents/invite`, {
        name: form.name.trim(),
        email: form.email.trim(),
      });
      setShowInvite(false);
      setForm({ name: "", email: "" });
      setShowSuccess(r.data);
      load();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const remove = async () => {
    await api.delete(`/organizer/events/${eventId}/agents/${showDelete.id}`);
    setShowDelete(null);
    load();
  };

  return (
    <div className="z-container" style={{ maxWidth: 640 }} data-testid="agents-manage-page">
      <Link to={`/org/event/${eventId}`} className="z-link" style={{ display: "inline-flex", alignItems: "center", gap: 4, marginBottom: 14, fontSize: 14 }}>
        <ArrowLeft size={14} /> Retour à l'événement
      </Link>
      <h1 className="z-page-title">Agents de contrôle</h1>
      <p className="z-page-sub">Les agents peuvent scanner les billets de cet événement.</p>

      <ZButton onClick={() => setShowInvite(true)} block data-testid="invite-agent-btn">
        <UserPlus size={16} /> Inviter un agent
      </ZButton>

      <div style={{ marginTop: 24, display: "grid", gap: 10 }}>
        {loading ? (
          <div style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>
        ) : agents.length === 0 ? (
          <div style={{ textAlign: "center", color: "var(--z-muted)", padding: 30 }} data-testid="agents-empty">
            Aucun agent invité.
          </div>
        ) : agents.map((a) => (
          <div key={a.id} className="z-card" style={{ padding: 14, display: "flex", alignItems: "center", gap: 12 }} data-testid={`agent-item-${a.id}`}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--z-teal)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
              {a.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{a.name}</div>
              <div style={{ fontSize: 12, color: "var(--z-muted)" }}>{a.email}</div>
              {a.must_change_password && <span className="z-badge" style={{ marginTop: 4 }}>Mot de passe à changer</span>}
            </div>
            <button onClick={() => setShowDelete(a)} className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error)" }} data-testid={`agent-remove-${a.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* Invite modal */}
      <Modal open={showInvite} onClose={() => setShowInvite(false)} title="Inviter un agent" testId="invite-agent-modal">
        <p style={{ color: "var(--z-muted)", fontSize: 14, marginBottom: 14 }}>
          Un compte agent sera créé avec un mot de passe temporaire.
        </p>
        <ZInput label="Nom complet" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="invite-agent-name" />
        <ZInput type="email" label="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="invite-agent-email" />
        {err && <p className="z-error-text" data-testid="invite-error">{err}</p>}
        <ZButton onClick={invite} block loading={submitting} data-testid="invite-confirm-btn">Inviter</ZButton>
      </Modal>

      {/* Success modal */}
      <Modal open={!!showSuccess} onClose={() => setShowSuccess(null)} title="Agent invité ✓" testId="invite-success-modal">
        <p style={{ fontSize: 14, marginBottom: 14 }}>
          <strong>{showSuccess?.name}</strong> peut maintenant scanner les billets.
        </p>
        {showSuccess?.temp_password ? (
          <>
            <p style={{ fontSize: 14, marginBottom: 8 }}>Mot de passe temporaire (à transmettre à l'agent) :</p>
            <div style={{ background: "var(--z-bg)", padding: 14, borderRadius: 12, fontFamily: "monospace", fontSize: 18, fontWeight: 700, color: "var(--z-teal)", textAlign: "center", letterSpacing: 1, marginBottom: 12 }} data-testid="invite-temp-password">
              {showSuccess.temp_password}
            </div>
            <ZButton variant="outline" block onClick={() => copy(showSuccess.temp_password)} data-testid="invite-copy-btn">
              <Copy size={16} /> {copied ? "Copié !" : "Copier"}
            </ZButton>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "rgba(218,113,1,0.10)", color: "var(--z-orange)", padding: 12, borderRadius: 10, fontSize: 13, marginTop: 12 }}>
              <AlertTriangle size={18} />
              <span>Ce mot de passe ne sera affiché qu'une seule fois. L'agent devra le changer à la première connexion.</span>
            </div>
          </>
        ) : (
          <p style={{ color: "var(--z-muted)", fontSize: 14 }}>{showSuccess?.message}</p>
        )}
      </Modal>

      {/* Delete confirm modal */}
      <Modal open={!!showDelete} onClose={() => setShowDelete(null)} title="Retirer cet agent ?" testId="agent-delete-modal">
        <p style={{ color: "var(--z-muted)", marginBottom: 18 }}>
          <strong>{showDelete?.name}</strong> ne pourra plus scanner les billets de cet événement.
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <ZButton variant="outline" block onClick={() => setShowDelete(null)} data-testid="agent-delete-cancel">Annuler</ZButton>
          <ZButton variant="danger" block onClick={remove} data-testid="agent-delete-confirm">Retirer</ZButton>
        </div>
      </Modal>
    </div>
  );
}
